/**
 * The ONE place that injects code into three's built-in materials (spec phase-2 §1). three is pinned to an exact
 * version because the chunk names used here (#include <…>) are internal API. Patching is idempotent and additive:
 * a material patched for snow and later for aerial perspective ends up with both, injected once.
 */
import * as THREE from 'three';
import { AERIAL_FRAG_APPLY, AERIAL_FRAG_PARS, AERIAL_VERT, AERIAL_VERT_PARS, AERIAL_UNIFORMS } from './fog';
import { SNOW_FRAG_COLOR, SNOW_FRAG_PARS, SNOW_FRAG_ROUGHNESS, SNOW_UNIFORMS, SNOW_VERT, SNOW_VERT_PARS } from './snow';

export interface PatchFeatures {
  snow?: boolean;
  aerial?: boolean;
}

interface PatchInfo {
  snow: boolean;
  aerial: boolean;
  base: THREE.Material;
}

const PATCH = 'tsukimachiPatch';

/** Shared uniform objects: every patched material reads the same values; update them once per frame. */
export const MATERIAL_UNIFORMS = { ...SNOW_UNIFORMS, ...AERIAL_UNIFORMS };

export function patchMaterial<T extends THREE.Material>(material: T, want: PatchFeatures): T {
  const prev = material.userData[PATCH] as PatchInfo | undefined;
  const snow = Boolean(want.snow || prev?.snow);
  const aerial = Boolean(want.aerial || prev?.aerial);
  if (prev && prev.snow === snow && prev.aerial === aerial) return material;
  const base = (prev?.base ?? material) as T;
  const out = base.clone() as T;
  const isStandard = out instanceof THREE.MeshStandardMaterial;
  const applySnow = snow && isStandard;
  const key = `tsk-${applySnow ? 's' : ''}${aerial ? 'a' : ''}`;
  out.userData = { ...base.userData, [PATCH]: { snow, aerial, base } satisfies PatchInfo };
  out.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, applySnow ? SNOW_UNIFORMS : {}, aerial ? AERIAL_UNIFORMS : {});
    let v = shader.vertexShader;
    let f = shader.fragmentShader;
    if (applySnow) {
      v = v.replace('#include <common>', `#include <common>\n${SNOW_VERT_PARS}`).replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>\n${SNOW_VERT}`);
      f = f
        .replace('#include <common>', `#include <common>\n${SNOW_FRAG_PARS}`)
        .replace('#include <color_fragment>', `#include <color_fragment>\n${SNOW_FRAG_COLOR}`)
        .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>\n${SNOW_FRAG_ROUGHNESS}`);
    }
    if (aerial) {
      v = v.replace('#include <common>', `#include <common>\n${AERIAL_VERT_PARS}`).replace('#include <project_vertex>', `#include <project_vertex>\n${AERIAL_VERT}`);
      f = f.replace('#include <common>', `#include <common>\n${AERIAL_FRAG_PARS}`).replace('#include <opaque_fragment>', `#include <opaque_fragment>\n${AERIAL_FRAG_APPLY}`);
    }
    shader.vertexShader = v;
    shader.fragmentShader = f;
  };
  out.customProgramCacheKey = () => key;
  return out;
}

/** Patches every mesh (and instanced mesh) material under root, except the sky dome and the moon. */
export function patchTree(root: THREE.Object3D, want: PatchFeatures, skip: (o: THREE.Object3D) => boolean = () => false): number {
  let n = 0;
  root.traverse((o) => {
    if (skip(o) || !(o instanceof THREE.Mesh)) return;
    const m = o.material;
    if (m instanceof THREE.MeshStandardMaterial || m instanceof THREE.MeshBasicMaterial) {
      o.material = patchMaterial(m, want);
      n++;
    }
  });
  return n;
}
